import type {
    DefinitionOutput,
    ExecutionUnitOutput,
    ExecutionUnitType,
} from "@magic-md/core";
import type {
    ExecutionCompletedEvent,
    ExecutionFailedEvent,
    ExecutionProgressEvent,
} from "@magic-md/engine-client/protocol";
import { t as translate, type LocaleParams } from "@magic-md/i18n";
import { create } from "zustand";
import {
    type PersistedOutputWatchDefinition,
    readOutputWatchIndex,
    writeOutputWatchIndex,
} from "../lib/output-watch-persistence";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { getPlatformService } from "../platform";
import { frontendLog } from "../utils/frontend-logger";
import { getNotificationBus } from "./notification-bus";

const DEFAULT_WATCH_PATH = "output";
const MAX_POINTS = 2_000;
const MAX_POINTS_PER_WATCH = 240;
const WATCH_SNAPSHOT_MAX_DEPTH = 6;
const WATCH_SNAPSHOT_MAX_NODES = 256;
const WATCH_SNAPSHOT_MAX_ARRAY_ITEMS = 50;
const WATCH_SNAPSHOT_MAX_OBJECT_KEYS = 40;
const WATCH_SNAPSHOT_MAX_STRING_LENGTH = 2_000;
const WATCH_SNAPSHOT_TRUNCATED_KEY = "__magicMdTruncated";

let nextWatchSeq = 1;
let nextPointSeq = 1;
const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();

function tr(key: string, params?: LocaleParams): string {
    return translate("editor", key, params);
}

export type OutputWatchEventKind = "completed" | "partial" | "failed";

export interface OutputWatch {
    id: string;
    definitionId?: string;
    documentPath?: string;
    sessionId: string;
    unitId: string;
    unitType?: ExecutionUnitType;
    path: string;
    label: string;
    enabled: boolean;
    includePartial: boolean;
    hasLastValue: boolean;
    lastHash?: string;
    lastValue?: unknown;
    lastChangedAt?: number;
    changeCount: number;
}

export interface OutputTracePoint {
    id: string;
    watchId: string;
    sessionId: string;
    unitId: string;
    unitType: ExecutionUnitType;
    path: string;
    label: string;
    timestamp: number;
    eventKind: OutputWatchEventKind;
    value: unknown;
    previousValue?: unknown;
    diffSummary: string;
    /** @deprecated Full outputs are intentionally not retained by the frontend timeline. */
    rawOutput?: unknown;
    error?: string;
    meta?: {
        actionName?: string;
        duration?: number;
    };
}

interface AddWatchInput {
    id?: string;
    definitionId?: string;
    documentPath?: string;
    sessionId: string;
    unitId: string;
    unitType?: ExecutionUnitType;
    path?: string;
    label?: string;
    enabled?: boolean;
    includePartial?: boolean;
    persist?: boolean;
}

interface HydrateDocumentWatchesInput {
    documentPath: string;
    sessionId?: string | null;
}

interface OutputWatchState {
    watches: OutputWatch[];
    points: OutputTracePoint[];
    selectedWatchId: string | null;
    selectedPointId: string | null;

    addWatch: (input: AddWatchInput) => string;
    hydrateDocumentWatches: (input: HydrateDocumentWatchesInput) => Promise<void>;
    removeWatch: (watchId: string) => void;
    toggleWatch: (watchId: string) => void;
    setWatchEnabled: (watchId: string, enabled: boolean) => void;
    setSelectedWatch: (watchId: string | null) => void;
    selectPersistedWatch: (documentPath: string, definitionId: string) => void;
    removePersistedWatch: (documentPath: string, definitionId: string) => Promise<void>;
    setSelectedPoint: (pointId: string | null) => void;
    clearPoints: () => void;
    clearPointsForWatch: (watchId: string) => void;
    reset: () => void;

    handleExecutionCompleted: (event: ExecutionCompletedEvent) => void;
    handleExecutionProgress: (event: ExecutionProgressEvent) => void;
    handleExecutionFailed: (event: ExecutionFailedEvent) => void;
}

function createWatchId(): string {
    return `watch-${nextWatchSeq++}`;
}

function slugWatchPart(value: string): string {
    const slug = value
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 72);
    return slug || "watch";
}

function createDefinitionId(unitId: string, path: string): string {
    return `watch_${slugWatchPart(`${unitId}_${path}`)}`;
}

function createPersistedRuntimeWatchId(documentPath: string, definitionId: string): string {
    return `persisted:${documentPath}:${definitionId}`;
}

function createDocumentFallbackSessionId(documentPath: string): string {
    return `document:${documentPath}`;
}

function createPointId(): string {
    return `trace-${nextPointSeq++}`;
}

function normalizeWatchPath(unitId: string, path: string | undefined): string {
    const trimmed = path?.trim() || DEFAULT_WATCH_PATH;
    if (trimmed === unitId) return DEFAULT_WATCH_PATH;
    if (trimmed.startsWith(`${unitId}.`)) {
        return trimmed.slice(unitId.length + 1) || DEFAULT_WATCH_PATH;
    }
    if (trimmed.startsWith("$ref{") && trimmed.endsWith("}")) {
        const inner = trimmed.slice(5, -1).trim();
        if (inner === unitId) return DEFAULT_WATCH_PATH;
        if (inner.startsWith(`${unitId}.`)) {
            return inner.slice(unitId.length + 1) || DEFAULT_WATCH_PATH;
        }
    }
    return trimmed;
}

function buildWatchLabel(unitId: string, path: string): string {
    return path === DEFAULT_WATCH_PATH ? `${unitId}.output` : `${unitId}.${path}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

export function stableOutputWatchHash(value: unknown): string {
    let hash = 0x811c9dc5;
    const seen = new WeakSet<object>();
    const write = (text: string) => {
        for (let index = 0; index < text.length; index += 1) {
            hash ^= text.charCodeAt(index);
            hash = Math.imul(hash, 0x01000193);
        }
    };
    const visit = (current: unknown) => {
        if (current === undefined) { write("undefined"); return; }
        if (current === null) { write("null"); return; }
        const valueType = typeof current;
        if (valueType === "string") {
            write(`string:${(current as string).length}:`);
            write(current as string);
            return;
        }
        if (valueType !== "object") {
            write(`${valueType}:${String(current)}`);
            return;
        }
        if (current instanceof Date) {
            write(`date:${current.toISOString()}`);
            return;
        }
        const object = current as object;
        if (seen.has(object)) { write("[Circular]"); return; }
        seen.add(object);
        if (Array.isArray(current)) {
            write(`array:${current.length}:[`);
            for (const item of current) visit(item);
            write("]");
            return;
        }
        if (isRecord(current)) {
            const keys = Object.keys(current).sort();
            write(`object:${keys.length}:{`);
            for (const key of keys) {
                write(`key:${key.length}:${key}`);
                visit(current[key]);
            }
            write("}");
            return;
        }
        write(String(current));
    };
    visit(value);
    return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function createOutputWatchSnapshot(value: unknown): unknown {
    const seen = new WeakSet<object>();
    let nodes = 0;

    const visit = (current: unknown, depth: number): unknown => {
        nodes += 1;
        if (nodes > WATCH_SNAPSHOT_MAX_NODES) return "[Truncated: node limit]";
        if (typeof current === "string") {
            return current.length > WATCH_SNAPSHOT_MAX_STRING_LENGTH
                ? `${current.slice(0, WATCH_SNAPSHOT_MAX_STRING_LENGTH)}… [${current.length - WATCH_SNAPSHOT_MAX_STRING_LENGTH} more chars]`
                : current;
        }
        if (current == null || typeof current !== "object") return current;
        if (current instanceof Date) return current.toISOString();
        if (depth >= WATCH_SNAPSHOT_MAX_DEPTH) return "[Truncated: depth limit]";
        if (seen.has(current)) return "[Circular]";
        seen.add(current);

        if (Array.isArray(current)) {
            const snapshot = current
                .slice(0, WATCH_SNAPSHOT_MAX_ARRAY_ITEMS)
                .map((item) => visit(item, depth + 1));
            if (current.length > WATCH_SNAPSHOT_MAX_ARRAY_ITEMS) {
                snapshot.push(`[Truncated: ${current.length - WATCH_SNAPSHOT_MAX_ARRAY_ITEMS} more items]`);
            }
            return snapshot;
        }

        if (isRecord(current)) {
            const keys = Object.keys(current).sort();
            const snapshot: Record<string, unknown> = {};
            for (const key of keys.slice(0, WATCH_SNAPSHOT_MAX_OBJECT_KEYS)) {
                snapshot[key] = visit(current[key], depth + 1);
            }
            if (keys.length > WATCH_SNAPSHOT_MAX_OBJECT_KEYS) {
                snapshot[WATCH_SNAPSHOT_TRUNCATED_KEY] =
                    `${keys.length - WATCH_SNAPSHOT_MAX_OBJECT_KEYS} more keys`;
            }
            return snapshot;
        }
        return String(current);
    };

    return visit(value, 0);
}

function parsePath(path: string): string[] {
    const trimmed = path.trim();
    if (!trimmed || trimmed === DEFAULT_WATCH_PATH) return [];

    const segments: string[] = [];
    let token = "";
    for (let i = 0; i < trimmed.length; i += 1) {
        const char = trimmed[i];
        if (char === ".") {
            if (token) segments.push(token);
            token = "";
            continue;
        }
        if (char === "[") {
            if (token) {
                segments.push(token);
                token = "";
            }
            const close = trimmed.indexOf("]", i + 1);
            if (close === -1) {
                token += char;
                continue;
            }
            const raw = trimmed.slice(i + 1, close).trim();
            if (
                (raw.startsWith("\"") && raw.endsWith("\"")) ||
                (raw.startsWith("'") && raw.endsWith("'"))
            ) {
                segments.push(raw.slice(1, -1));
            } else if (raw) {
                segments.push(raw);
            }
            i = close;
            continue;
        }
        token += char;
    }
    if (token) segments.push(token);
    return segments.filter(Boolean);
}

function readSegments(root: unknown, segments: string[]): unknown {
    let current = root;
    for (const segment of segments) {
        if (current == null) return undefined;
        if (Array.isArray(current)) {
            const index = Number(segment);
            if (!Number.isInteger(index)) return undefined;
            current = current[index];
            continue;
        }
        if (typeof current !== "object") return undefined;
        current = (current as Record<string, unknown>)[segment];
    }
    return current;
}

export function getOutputWatchRootValue(output: ExecutionUnitOutput): unknown {
    switch (output.unitType) {
        case "definition":
            return (output as DefinitionOutput).data;
        case "content":
            return output.data;
        case "for":
            return { iterations: output.iterations };
        case "import":
            return {
                symbols: output.symbols,
                ...(output.document ? { document: output.document } : {}),
            };
        case "use":
            return {
                materializedId: output.materializedId,
                sourceName: output.sourceName,
                kind: output.kind,
                ...(output.materializedBody ? { materializedBody: output.materializedBody } : {}),
                ...(output.nodeOutput ? { nodeOutput: output.nodeOutput } : {}),
                ...(output.contentOutput ? { contentOutput: output.contentOutput } : {}),
                ...(output.forOutput ? { forOutput: output.forOutput } : {}),
            };
    }
}

export function readOutputWatchValue(
    output: ExecutionUnitOutput,
    path: string,
): unknown {
    const trimmed = path.trim();
    if (!trimmed || trimmed === DEFAULT_WATCH_PATH) {
        return getOutputWatchRootValue(output);
    }

    if (
        (output.unitType === "definition" || output.unitType === "content") &&
        trimmed.startsWith("output.data.")
    ) {
        return readSegments(output, parsePath(trimmed.slice("output.".length)));
    }

    if (trimmed.startsWith("output.")) {
        return readSegments(
            getOutputWatchRootValue(output),
            parsePath(trimmed.slice("output.".length)),
        );
    }

    if (trimmed.startsWith("raw.")) {
        return readSegments(output, parsePath(trimmed.slice("raw.".length)));
    }

    return readSegments(getOutputWatchRootValue(output), parsePath(trimmed));
}

function readPartialWatchValue(partialOutput: Record<string, unknown>, path: string): unknown {
    const trimmed = path.trim();
    if (!trimmed || trimmed === DEFAULT_WATCH_PATH) return partialOutput;
    if (trimmed.startsWith("output.")) {
        return readSegments(partialOutput, parsePath(trimmed.slice("output.".length)));
    }
    if (trimmed.startsWith("raw.")) {
        return readSegments(partialOutput, parsePath(trimmed.slice("raw.".length)));
    }
    return readSegments(partialOutput, parsePath(trimmed));
}

export function summarizeWatchValue(value: unknown): string {
    if (value === undefined) return "undefined";
    if (value === null) return "null";
    if (typeof value === "string") {
        return value.length > 40 ? `${JSON.stringify(value.slice(0, 37))}...` : JSON.stringify(value);
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) return tr("watch.value.array", { count: value.length });
    if (isRecord(value)) return tr("watch.value.object", { count: Object.keys(value).length });
    return String(value);
}

function diffSummary(previousValue: unknown, nextValue: unknown, hadPrevious: boolean): string {
    if (!hadPrevious) {
        return tr("watch.summary.initial", { value: summarizeWatchValue(nextValue) });
    }
    if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
        if (previousValue.length !== nextValue.length) {
            return tr("watch.summary.arrayLength", {
                previous: previousValue.length,
                next: nextValue.length,
            });
        }
        return tr("watch.summary.arrayChanged", { count: nextValue.length });
    }
    if (isRecord(previousValue) && isRecord(nextValue)) {
        const prevKeys = new Set(Object.keys(previousValue));
        const nextKeys = new Set(Object.keys(nextValue));
        const added = [...nextKeys].filter((key) => !prevKeys.has(key));
        const removed = [...prevKeys].filter((key) => !nextKeys.has(key));
        const changed = [...nextKeys].filter((key) => prevKeys.has(key) && previousValue[key] !== nextValue[key]);
        const parts = [
            added.length ? `+${added.length}` : "",
            removed.length ? `-${removed.length}` : "",
            changed.length ? `~${changed.length}` : "",
        ].filter(Boolean);
        return parts.length
            ? tr("watch.summary.objectKeys", { parts: parts.join(" ") })
            : tr("watch.summary.objectChanged");
    }
    return `${summarizeWatchValue(previousValue)} -> ${summarizeWatchValue(nextValue)}`;
}

function trimPoints(points: OutputTracePoint[]): OutputTracePoint[] {
    let next = points;
    if (next.length > MAX_POINTS) next = next.slice(next.length - MAX_POINTS);
    const counts = new Map<string, number>();
    const kept: OutputTracePoint[] = [];
    for (let i = next.length - 1; i >= 0; i -= 1) {
        const point = next[i];
        const count = counts.get(point.watchId) ?? 0;
        if (count >= MAX_POINTS_PER_WATCH) continue;
        counts.set(point.watchId, count + 1);
        kept.push(point);
    }
    return kept.reverse();
}

function activeWatchesForEvent(
    watches: OutputWatch[],
    sessionId: string,
    unitId: string,
): OutputWatch[] {
    return watches.filter(
        (watch) =>
            watch.enabled &&
            watch.sessionId === sessionId &&
            watch.unitId === unitId,
    );
}

export function collectWatchedUnitIdsForDocument(
    watches: OutputWatch[],
    context: { documentPath?: string | null; sessionId?: string | null },
): Set<string> {
    const documentPath = context.documentPath?.trim() || null;
    const sessionId = context.sessionId?.trim() || null;
    const ids = new Set<string>();
    for (const watch of watches) {
        if (!watch.enabled) continue;
        const documentMatches =
            !!documentPath && watch.documentPath === documentPath;
        const sessionMatches =
            !!sessionId &&
            watch.sessionId === sessionId &&
            (!watch.documentPath || !documentPath || watch.documentPath === documentPath);
        if (documentMatches || sessionMatches) {
            ids.add(watch.unitId);
        }
    }
    return ids;
}

function toPersistedDefinition(watch: OutputWatch): PersistedOutputWatchDefinition | null {
    if (!watch.documentPath) return null;
    return {
        id: watch.definitionId ?? watch.id,
        unitId: watch.unitId,
        path: watch.path,
        label: watch.label,
        includePartial: watch.includePartial,
        enabled: watch.enabled,
    };
}

function dispatchOutputWatchesRefresh(documentPath: string): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent("magic-md:bundle-refresh", {
            detail: {
                filePath: documentPath,
                bundleDir: getDocumentBundleDir(documentPath),
            },
        }),
    );
}

async function persistDocumentWatches(
    documentPath: string,
    watches: OutputWatch[],
): Promise<void> {
    const definitions = watches
        .filter((watch) => watch.documentPath === documentPath)
        .map(toPersistedDefinition)
        .filter((watch): watch is PersistedOutputWatchDefinition => !!watch);
    await writeOutputWatchIndex(
        getPlatformService().engine,
        documentPath,
        definitions,
    );
    dispatchOutputWatchesRefresh(documentPath);
}

function schedulePersistDocumentWatches(
    documentPath: string | undefined,
    get: () => OutputWatchState,
): void {
    if (!documentPath) return;
    const existing = persistTimers.get(documentPath);
    if (existing) globalThis.clearTimeout(existing);
    const timer = globalThis.setTimeout(() => {
        persistTimers.delete(documentPath);
        void persistDocumentWatches(documentPath, get().watches).catch((err) => {
            frontendLog.warn("Failed to persist output watches", err, {
                source: "system",
            });
        });
    }, 0);
    persistTimers.set(documentPath, timer);
}

export const useOutputWatchStore = create<OutputWatchState>((set, get) => ({
    watches: [],
    points: [],
    selectedWatchId: null,
    selectedPointId: null,

    addWatch: (input) => {
        const sessionId = input.sessionId.trim();
        const unitId = input.unitId.trim();
        const documentPath = input.documentPath?.trim() || undefined;
        if (!sessionId || !unitId) {
            throw new Error("Output watch requires sessionId and unitId");
        }
        const path = normalizeWatchPath(unitId, input.path);
        const definitionId = input.definitionId?.trim() ||
            input.id?.trim() ||
            (documentPath ? createDefinitionId(unitId, path) : undefined);
        const runtimeId = documentPath && definitionId
            ? createPersistedRuntimeWatchId(documentPath, definitionId)
            : input.id?.trim() || createWatchId();
        const existing = get().watches.find(
            (watch) =>
                watch.documentPath === documentPath &&
                watch.unitId === unitId &&
                watch.path === path &&
                (documentPath || watch.sessionId === sessionId),
        );
        if (existing) {
            set((state) => ({
                watches: state.watches.map((watch) =>
                    watch.id === existing.id
                        ? {
                            ...watch,
                            sessionId,
                            unitType: input.unitType ?? watch.unitType,
                            enabled: input.enabled ?? true,
                            definitionId: definitionId ?? watch.definitionId,
                            documentPath,
                            includePartial: input.includePartial ?? watch.includePartial,
                            label: input.label?.trim() || watch.label,
                        }
                        : watch,
                ),
            }));
            if (input.persist !== false) {
                schedulePersistDocumentWatches(documentPath, get);
            }
            return existing.id;
        }

        const watch: OutputWatch = {
            id: runtimeId,
            definitionId,
            documentPath,
            sessionId,
            unitId,
            unitType: input.unitType,
            path,
            label: input.label?.trim() || buildWatchLabel(unitId, path),
            enabled: input.enabled ?? true,
            includePartial: input.includePartial ?? false,
            hasLastValue: false,
            changeCount: 0,
        };
        set((state) => ({ watches: [...state.watches, watch] }));
        if (input.persist !== false) {
            schedulePersistDocumentWatches(documentPath, get);
        }
        return watch.id;
    },

    hydrateDocumentWatches: async ({ documentPath, sessionId }) => {
        const normalizedDocumentPath = documentPath.trim();
        const normalizedSessionId = sessionId?.trim() ||
            createDocumentFallbackSessionId(normalizedDocumentPath);
        if (!normalizedDocumentPath) return;
        const index = await readOutputWatchIndex(
            getPlatformService().engine,
            normalizedDocumentPath,
        );
        set((state) => {
            const nextWatches = [...state.watches];
            for (const definition of index.watches) {
                const path = normalizeWatchPath(definition.unitId, definition.path);
                const runtimeId = createPersistedRuntimeWatchId(
                    normalizedDocumentPath,
                    definition.id,
                );
                const existingIndex = nextWatches.findIndex((watch) => watch.id === runtimeId);
                const patch: OutputWatch = {
                    id: runtimeId,
                    definitionId: definition.id,
                    documentPath: normalizedDocumentPath,
                    sessionId: normalizedSessionId,
                    unitId: definition.unitId,
                    path,
                    label: definition.label || buildWatchLabel(definition.unitId, path),
                    enabled: definition.enabled !== false,
                    includePartial: definition.includePartial,
                    hasLastValue: false,
                    changeCount: 0,
                };
                if (existingIndex === -1) {
                    nextWatches.push(patch);
                } else {
                    const existing = nextWatches[existingIndex];
                    nextWatches[existingIndex] = {
                        ...existing,
                        ...patch,
                        unitType: existing.unitType,
                        hasLastValue: existing.hasLastValue,
                        lastHash: existing.lastHash,
                        lastValue: existing.lastValue,
                        lastChangedAt: existing.lastChangedAt,
                        changeCount: existing.changeCount,
                    };
                }
            }
            return { watches: nextWatches };
        });
    },

    removeWatch: (watchId) => {
        let documentPath: string | undefined;
        set((state) => {
            const target = state.watches.find((watch) => watch.id === watchId);
            documentPath = target?.documentPath;
            return {
                watches: state.watches.filter((watch) => watch.id !== watchId),
                points: state.points.filter((point) => point.watchId !== watchId),
                selectedPointId:
                    state.selectedPointId &&
                    state.points.some((point) => point.id === state.selectedPointId && point.watchId === watchId)
                        ? null
                        : state.selectedPointId,
                selectedWatchId: state.selectedWatchId === watchId ? null : state.selectedWatchId,
            };
        });
        schedulePersistDocumentWatches(documentPath, get);
    },

    toggleWatch: (watchId) => {
        let documentPath: string | undefined;
        set((state) => ({
            watches: state.watches.map((watch) => {
                if (watch.id !== watchId) return watch;
                documentPath = watch.documentPath;
                return { ...watch, enabled: !watch.enabled };
            }),
        }));
        schedulePersistDocumentWatches(documentPath, get);
    },

    setWatchEnabled: (watchId, enabled) => {
        let documentPath: string | undefined;
        set((state) => ({
            watches: state.watches.map((watch) => {
                if (watch.id !== watchId) return watch;
                documentPath = watch.documentPath;
                return { ...watch, enabled };
            }),
        }));
        schedulePersistDocumentWatches(documentPath, get);
    },

    setSelectedWatch: (watchId) =>
        set((state) => {
            if (!watchId) {
                return { selectedWatchId: null, selectedPointId: null };
            }
            const selectedPointMatchesWatch = state.selectedPointId
                ? state.points.some((point) =>
                    point.id === state.selectedPointId && point.watchId === watchId
                )
                : false;
            return {
                selectedWatchId: watchId,
                selectedPointId: selectedPointMatchesWatch ? state.selectedPointId : null,
            };
        }),

    selectPersistedWatch: (documentPath, definitionId) => {
        const runtimeId = createPersistedRuntimeWatchId(documentPath, definitionId);
        set({ selectedWatchId: runtimeId, selectedPointId: null });
    },

    removePersistedWatch: async (documentPath, definitionId) => {
        const normalizedDocumentPath = documentPath.trim();
        const normalizedDefinitionId = definitionId.trim();
        if (!normalizedDocumentPath || !normalizedDefinitionId) return;
        const runtimeId = createPersistedRuntimeWatchId(
            normalizedDocumentPath,
            normalizedDefinitionId,
        );
        set((state) => ({
            watches: state.watches.filter((watch) =>
                watch.id !== runtimeId &&
                !(
                    watch.documentPath === normalizedDocumentPath &&
                    watch.definitionId === normalizedDefinitionId
                )
            ),
            points: state.points.filter((point) => point.watchId !== runtimeId),
            selectedWatchId: state.selectedWatchId === runtimeId ? null : state.selectedWatchId,
            selectedPointId:
                state.selectedPointId &&
                    state.points.some((point) => point.id === state.selectedPointId && point.watchId === runtimeId)
                    ? null
                    : state.selectedPointId,
        }));
        const index = await readOutputWatchIndex(
            getPlatformService().engine,
            normalizedDocumentPath,
        );
        await writeOutputWatchIndex(
            getPlatformService().engine,
            normalizedDocumentPath,
            index.watches.filter((watch) => watch.id !== normalizedDefinitionId),
        );
        dispatchOutputWatchesRefresh(normalizedDocumentPath);
    },

    setSelectedPoint: (pointId) => set({ selectedPointId: pointId }),

    clearPoints: () =>
        set((state) => ({
            points: [],
            selectedPointId: null,
            watches: state.watches.map((watch) => ({
                ...watch,
                hasLastValue: false,
                lastHash: undefined,
                lastValue: undefined,
                lastChangedAt: undefined,
                changeCount: 0,
            })),
        })),

    clearPointsForWatch: (watchId) =>
        set((state) => ({
            points: state.points.filter((point) => point.watchId !== watchId),
            selectedPointId:
                state.selectedPointId &&
                    state.points.some((point) => point.id === state.selectedPointId && point.watchId === watchId)
                    ? null
                    : state.selectedPointId,
            watches: state.watches.map((watch) =>
                watch.id === watchId
                    ? {
                        ...watch,
                        hasLastValue: false,
                        lastHash: undefined,
                        lastValue: undefined,
                        lastChangedAt: undefined,
                        changeCount: 0,
                    }
                    : watch,
            ),
        })),

    reset: () => {
        for (const timer of persistTimers.values()) {
            globalThis.clearTimeout(timer);
        }
        persistTimers.clear();
        set({ watches: [], points: [], selectedWatchId: null, selectedPointId: null });
    },

    handleExecutionCompleted: (event) => {
        const matches = activeWatchesForEvent(get().watches, event.sessionId, event.unitId);
        if (matches.length === 0) return;
        const timestamp = Date.parse(event.meta.executedAt) || Date.now();

        set((state) => {
            let changed = false;
            const pointByWatch = new Map<string, OutputTracePoint>();
            const nextWatches = state.watches.map((watch) => {
                if (!matches.some((match) => match.id === watch.id)) return watch;

                const value = readOutputWatchValue(event.output, watch.path);
                const hash = stableOutputWatchHash(value);
                if (watch.hasLastValue && watch.lastHash === hash) return watch;
                const snapshot = createOutputWatchSnapshot(value);

                changed = true;
                const point: OutputTracePoint = {
                    id: createPointId(),
                    watchId: watch.id,
                    sessionId: event.sessionId,
                    unitId: event.unitId,
                    unitType: event.unitType,
                    path: watch.path,
                    label: watch.label,
                    timestamp,
                    eventKind: "completed",
                    value: snapshot,
                    previousValue: watch.hasLastValue ? watch.lastValue : undefined,
                    diffSummary: diffSummary(watch.lastValue, snapshot, watch.hasLastValue),
                    meta: {
                        actionName: event.meta.actionName,
                        duration: event.meta.duration,
                    },
                };
                pointByWatch.set(watch.id, point);
                return {
                    ...watch,
                    unitType: event.unitType,
                    hasLastValue: true,
                    lastHash: hash,
                    lastValue: snapshot,
                    lastChangedAt: timestamp,
                    changeCount: watch.changeCount + 1,
                };
            });

            if (!changed) return {};
            const nextPoints = trimPoints([...state.points, ...pointByWatch.values()]);
            return { watches: nextWatches, points: nextPoints };
        });
    },

    handleExecutionProgress: (event) => {
        if (!event.partialOutput) return;
        const matches = activeWatchesForEvent(get().watches, event.sessionId, event.unitId)
            .filter((watch) => watch.includePartial);
        if (matches.length === 0) return;
        const timestamp = Date.now();

        set((state) => {
            let changed = false;
            const pointByWatch = new Map<string, OutputTracePoint>();
            const nextWatches = state.watches.map((watch) => {
                if (!matches.some((match) => match.id === watch.id)) return watch;
                const value = readPartialWatchValue(event.partialOutput ?? {}, watch.path);
                const hash = stableOutputWatchHash(value);
                if (watch.hasLastValue && watch.lastHash === hash) return watch;
                const snapshot = createOutputWatchSnapshot(value);

                changed = true;
                const point: OutputTracePoint = {
                    id: createPointId(),
                    watchId: watch.id,
                    sessionId: event.sessionId,
                    unitId: event.unitId,
                    unitType: event.unitType,
                    path: watch.path,
                    label: watch.label,
                    timestamp,
                    eventKind: "partial",
                    value: snapshot,
                    previousValue: watch.hasLastValue ? watch.lastValue : undefined,
                    diffSummary: diffSummary(watch.lastValue, snapshot, watch.hasLastValue),
                };
                pointByWatch.set(watch.id, point);
                return {
                    ...watch,
                    unitType: event.unitType,
                    hasLastValue: true,
                    lastHash: hash,
                    lastValue: snapshot,
                    lastChangedAt: timestamp,
                    changeCount: watch.changeCount + 1,
                };
            });

            if (!changed) return {};
            return {
                watches: nextWatches,
                points: trimPoints([...state.points, ...pointByWatch.values()]),
            };
        });
    },

    handleExecutionFailed: (event) => {
        const matches = activeWatchesForEvent(get().watches, event.sessionId, event.unitId);
        if (matches.length === 0) return;
        const timestamp = Date.now();
        set((state) => {
            const points = matches.map((watch) => ({
                id: createPointId(),
                watchId: watch.id,
                sessionId: event.sessionId,
                unitId: event.unitId,
                unitType: event.unitType,
                path: watch.path,
                label: watch.label,
                timestamp,
                eventKind: "failed" as const,
                value: { error: event.error },
                previousValue: watch.hasLastValue ? watch.lastValue : undefined,
                diffSummary: tr("watch.summary.failed", { error: event.error }),
                error: event.error,
                meta: {
                    actionName: event.actionName,
                },
            }));
            return {
                watches: state.watches.map((watch) =>
                    matches.some((match) => match.id === watch.id)
                        ? {
                            ...watch,
                            unitType: event.unitType,
                            lastChangedAt: timestamp,
                            changeCount: watch.changeCount + 1,
                        }
                        : watch,
                ),
                points: trimPoints([...state.points, ...points]),
            };
        });
    },
}));

let registeredBus: ReturnType<typeof getNotificationBus> | null = null;
let unregisterOutputWatchHandlers: (() => void)[] = [];

export function ensureOutputWatchNotificationHandlers(): void {
    const bus = getNotificationBus();
    if (registeredBus === bus) return;
    for (const unregister of unregisterOutputWatchHandlers) unregister();
    unregisterOutputWatchHandlers = [
        bus.subscribe<ExecutionCompletedEvent>("event/execution.completed", (event) => {
            useOutputWatchStore.getState().handleExecutionCompleted(event);
        }),
        bus.subscribe<ExecutionProgressEvent>("event/execution.progress", (event) => {
            useOutputWatchStore.getState().handleExecutionProgress(event);
        }),
        bus.subscribe<ExecutionFailedEvent>("event/execution.failed", (event) => {
            useOutputWatchStore.getState().handleExecutionFailed(event);
        }),
    ];
    registeredBus = bus;
}
